/**
 * Time Clock — Phase 1
 *
 * Premium daily-view timeclock similar to Connecteam, optimized for a
 * cleaning company.
 *
 * Data lives in `localStorage.maidly_timeEntries`. Each entry:
 *   {
 *     id, employeeId, employeeName,
 *     startTime, endTime,          // ISO strings
 *     hours, pay, hourlyRate,
 *     note, project, task,
 *     job,                          // e.g. "House Cleaning"
 *     gpsClockIn, gpsClockOut,      // { lat, lng, accuracy } | null
 *     breaks                        // [{ startTime, endTime|null }]
 *   }
 *
 * AutoSync (already running app-wide) handles cloud push automatically
 * because the `maidly_*` key prefix matches its watch pattern. No new
 * backend endpoints needed for Phase 1.
 *
 * Phase 2 (next session): Timesheets tab with weekly editable shifts +
 * approval workflow + audit logs.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Clock, Search, Plus, Download, MapPin, Play,
  Square, Sun, Moon, Coffee,
  Users as UsersIcon, X, LayoutGrid, ClipboardList,
  DollarSign, BarChart3, AlertTriangle, MessageSquare,
  CheckCircle2, Hourglass, CalendarCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  getCrewName,
  getCrewColor,
} from '../services/CrewService';
import TimesheetsView from '../components/timeclock/TimesheetsView';
import { PayrollView, InsightsView } from '../components/timeclock/PayrollAndInsights';
import { IconTile, TINT } from '../components/ui/IconTile';
import EmployeeDrawer from '../components/timeclock/EmployeeDrawer';
import { PTO_KEY, normalizeEntry, auditEvent, detectConflicts, startOfWeek, computeWeeklyStats } from '../utils/timeClockHelpers';
import { KpiCard, KpiRow } from '../components/timeclock/KpiCards';
import useCloudSync from '../hooks/useCloudSync';
import realtimeService from '../services/RealtimeService';
import { useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'maidly_timeEntries';
const DARK_KEY = 'maidly_timeclock_dark';
const REFRESH_MS = 30 * 1000; // live refresh — picks up clock-ins from other devices

const pad2 = (n) => String(n).padStart(2, '0');
const toISODate = (d) => d.toISOString().slice(0, 10);
const formatHM = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const formatDuration = (mins) => {
  if (!mins || mins <= 0) return '00:00';
  const h = Math.floor(mins / 60);
  const m = Math.floor(mins % 60);
  return `${pad2(h)}:${pad2(m)}`;
};

// Initials from a name like "Ana Lopez" → "AL"
const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

// Stable color per employee id (subtle, avatar background)
const avatarColor = (id = '') => {
  const palette = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#ec4899', '#0ea5e9', '#22c55e', '#ef4444'];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
};

// minutes elapsed between two iso timestamps; if `end` missing, use Date.now()
const minutesBetween = (startIso, endIso) => {
  if (!startIso) return 0;
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  return Math.max(0, (end - start) / 60000);
};

// Sum break minutes (only completed breaks count for total; an open break
// still ticks the live "On Break" status but its minutes are excluded from
// total until the user resumes).
const totalBreakMinutes = (breaks = []) =>
  breaks.reduce((acc, b) => acc + (b.endTime ? minutesBetween(b.startTime, b.endTime) : 0), 0);

// Computed live total worked minutes for a single entry (today's shift).
const liveWorkedMinutes = (entry) => {
  if (!entry?.startTime) return 0;
  const gross = minutesBetween(entry.startTime, entry.endTime);
  return Math.max(0, gross - totalBreakMinutes(entry.breaks));
};

// "Working" | "On Break" | "Off"
const computeStatus = (entry) => {
  if (!entry) return 'Off';
  if (entry.endTime) return 'Off';
  const open = (entry.breaks || []).find((b) => !b.endTime);
  return open ? 'On Break' : 'Working';
};

const TimeTracking = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [crewFilter, setCrewFilter] = useState('');
  const [search, setSearch] = useState('');
  const [date, setDate] = useState(toISODate(new Date()));
  const [showAddShift, setShowAddShift] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem(DARK_KEY) === '1');
  // Phase 2: tabs + PTO requests
  const [tab, setTab] = useState('today'); // 'today' | 'timesheets'
  const [ptoRequests, setPtoRequests] = useState([]);
  // Re-render every second for the live "Total Hours" + clock display.
  const [, setTick] = useState(0);
  // Employee drawer state (clicked employee row → slide-over)
  const [drawerEmpId, setDrawerEmpId] = useState(null);
  // Track mobile breakpoint for drawer mode (fullsheet vs side)
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 720,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 720);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const isManager = user?.role === 'manager';
  const isEmployee = user?.role === 'employee';

  // ----- data load + live refresh -----
  // Reads from localStorage + state setters (which React guarantees stable),
  // so empty deps is correct. Don't add setEntries/setEmployees/setPtoRequests
  // here — that would just churn the closure on every render.
  const load = useCallback(() => {
    try {
      const allEntries = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const allEmployees = JSON.parse(localStorage.getItem('maidly_employees') || '[]');
      const allPto = JSON.parse(localStorage.getItem(PTO_KEY) || '[]');
      // Normalize so Phase-2 fields (approvalStatus, auditLog, attachments,
      // employeeNote, managerNote, type) are always present — even for entries
      // created before Phase 2 shipped.
      setEntries(allEntries.map(normalizeEntry));
      setEmployees(allEmployees.filter((e) => e.status !== 'inactive'));
      setPtoRequests(allPto);
    } catch {
      setEntries([]);
      setEmployees([]);
      setPtoRequests([]);
    }
  }, []);

  useEffect(() => {
    load();
    const refresh = setInterval(load, REFRESH_MS);
    const tick = setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      clearInterval(refresh);
      clearInterval(tick);
    };
  }, [load]);

  // Realtime: refresh immediately when another device clocks in/out
  useCloudSync(load, ['timeEntries', 'employees']);

  useEffect(() => {
    localStorage.setItem(DARK_KEY, dark ? '1' : '0');
  }, [dark]);

  // ----- role-scoped employee list -----
  const visibleEmployees = useMemo(() => {
    let list = employees;
    if (isEmployee) {
      list = list.filter((e) => e.email === user?.email || e.id === user?.id);
      // If the authenticated employee has no record in the legacy
      // `maidly_employees` seed (common for newly-onboarded teammates or the
      // multi-tenant test workspace accounts), synthesise a self-row from the
      // auth user so they can still see their own Clock In / Clock Out.
      if (list.length === 0 && user) {
        list = [{
          id: user.id,
          name: user.name || user.email,
          email: user.email,
          role: 'employee',
          status: 'active',
          crewId: null,
        }];
      }
    } else if (isManager) {
      // Manager sees their own crew (if any), else all
      const me = employees.find((e) => e.email === user?.email);
      if (me?.crewId) list = list.filter((e) => e.crewId === me.crewId);
    }
    if (crewFilter) list = list.filter((e) => e.crewId === crewFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((e) => e.name?.toLowerCase().includes(q));
    }
    return list;
  }, [employees, isEmployee, isManager, user, crewFilter, search]);

  // Index today's entries by employeeId (most recent first if multiple).
  // PTO / sick / unpaid entries are excluded from the Today live table — they
  // belong in the Timesheets view; showing them here with a Clock-In button
  // would confuse admins.
  const entryByEmpId = useMemo(() => {
    const target = date;
    const map = {};
    entries.forEach((e) => {
      if (e.type && e.type !== 'shift') return;
      const d = toISODate(new Date(e.startTime));
      if (d !== target) return;
      const prior = map[e.employeeId];
      if (!prior || new Date(e.startTime) > new Date(prior.startTime)) {
        map[e.employeeId] = e;
      }
    });
    return map;
  }, [entries, date]);

  // Crews available in the visible employee set (for filter dropdown)
  const crewsInScope = useMemo(() => {
    const map = new Map();
    employees.forEach((e) => {
      if (e.crewId) map.set(e.crewId, true);
    });
    return Array.from(map.keys());
  }, [employees]);

  // ----- persistence -----
  // Functional setState — protects against closure-captured `entries` going
  // stale during rapid-fire actions (e.g. concurrent clock-ins from
  // auto-refresh while a user is also clicking a button).
  const saveEntries = (updater) => {
    setEntries((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  // Same pattern for PTO requests — keep in lockstep with entries.
  const savePtoRequests = (updater) => {
    setPtoRequests((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      localStorage.setItem(PTO_KEY, JSON.stringify(next));
      return next;
    });
  };

  // Persist employee mutations (used by the editable Payroll table when the
  // manager changes someone's hourly rate). Mirrors saveEntries so AutoSync
  // picks up the write.
  const saveEmployees = (updater) => {
    setEmployees((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      localStorage.setItem('maidly_employees', JSON.stringify(next));
      return next;
    });
  };

  // ----- GPS capture (best-effort, never blocks the clock action) -----
  const captureGPS = () =>
    new Promise((resolve) => {
      if (!('geolocation' in navigator)) return resolve(null);
      const timeout = setTimeout(() => resolve(null), 4000);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(timeout);
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: Math.round(pos.coords.accuracy || 0),
          });
        },
        () => {
          clearTimeout(timeout);
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 4000, maximumAge: 60000 }
      );
    });

  // ----- actions -----
  const handleClockIn = async (emp) => {
    // Block double clock-in
    if (entries.some((e) => e.employeeId === emp.id && !e.endTime)) {
      toast.error(`${emp.name} is already clocked in`);
      return;
    }
    const gps = await captureGPS();
    const entry = normalizeEntry({
      id: `t_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      employeeId: emp.id,
      employeeName: emp.name,
      startTime: new Date().toISOString(),
      endTime: null,
      hours: 0,
      pay: 0,
      hourlyRate: parseFloat(emp.hourlyRate) || 0,
      note: '',
      project: 'General Work',
      task: 'Regular Hours',
      job: '',
      gpsClockIn: gps,
      gpsClockOut: null,
      breaks: [],
      auditLog: [auditEvent(user, 'created')],
    });
    saveEntries((prev) => [...prev, entry]);
    // Broadcast presence: I'm working now
    realtimeService.setStatus('working');
    toast.success(`${emp.name} clocked in${gps ? ' (GPS verified)' : ''}`);
  };

  const handleClockOut = async (entry) => {
    const gps = await captureGPS();
    const now = new Date();
    // Close any open break first
    const breaks = (entry.breaks || []).map((b) => (b.endTime ? b : { ...b, endTime: now.toISOString() }));
    const breakMins = totalBreakMinutes(breaks);
    const gross = minutesBetween(entry.startTime, now.toISOString());
    const net = Math.max(0, gross - breakMins);
    const hours = parseFloat((net / 60).toFixed(2));
    const pay = parseFloat((hours * (entry.hourlyRate || 0)).toFixed(2));
    saveEntries((prev) =>
      prev.map((e) =>
        e.id === entry.id
          ? {
              ...e,
              endTime: now.toISOString(),
              breaks, hours, pay,
              gpsClockOut: gps,
              auditLog: [...(e.auditLog || []), auditEvent(user, 'clocked-out')],
            }
          : e
      )
    );
    toast.success(`${entry.employeeName} clocked out — ${formatDuration(net)} worked`);
    realtimeService.setStatus('online');
  };

  const handleStartBreak = (entry) => {
    if ((entry.breaks || []).some((b) => !b.endTime)) return;
    const newBreak = { startTime: new Date().toISOString(), endTime: null };
    saveEntries((prev) =>
      prev.map((e) => (e.id === entry.id ? { ...e, breaks: [...(e.breaks || []), newBreak] } : e))
    );
    toast.info(`${entry.employeeName} on break`);
    realtimeService.setStatus('on_break');
  };

  const handleEndBreak = (entry) => {
    const now = new Date().toISOString();
    saveEntries((prev) =>
      prev.map((e) => {
        if (e.id !== entry.id) return e;
        const breaks = (e.breaks || []).map((b, i, arr) =>
          i === arr.length - 1 && !b.endTime ? { ...b, endTime: now } : b
        );
        return { ...e, breaks };
      })
    );
    toast.success(`${entry.employeeName} resumed work`);
    realtimeService.setStatus('working');
  };

  // CSV payroll export — admin/manager only
  const handleExportPayroll = () => {
    const todays = visibleEmployees
      .map((emp) => {
        const e = entryByEmpId[emp.id];
        const mins = e ? liveWorkedMinutes(e) : 0;
        const breakMins = e ? totalBreakMinutes(e.breaks) : 0;
        const hours = mins / 60;
        const rate = parseFloat(emp.hourlyRate) || 0;
        const pay = hours * rate;
        return {
          name: emp.name,
          role: emp.role || '',
          crew: getCrewName(emp.crewId) || '',
          clockIn: e?.startTime ? formatHM(e.startTime) : '',
          clockOut: e?.endTime ? formatHM(e.endTime) : '',
          breakMin: Math.round(breakMins),
          totalHours: hours.toFixed(2),
          hourlyRate: rate.toFixed(2),
          pay: pay.toFixed(2),
          job: e?.job || '',
        };
      });
    const headers = ['Name', 'Role', 'Crew', 'Clock In', 'Clock Out', 'Break (min)', 'Total Hours', 'Hourly Rate', 'Pay', 'Job'];
    const rows = todays.map((r) => [
      r.name, r.role, r.crew, r.clockIn, r.clockOut, r.breakMin, r.totalHours, r.hourlyRate, r.pay, r.job,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll_${date}.csv`;
    // Chromium / Firefox require the anchor to be in the DOM for `download` to
    // honor the file name and trigger the download dialog.
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Payroll exported');
  };

  // Manual shift entry (for admins to backfill or correct)
  const handleAddShift = (payload) => {
    const emp = employees.find((e) => e.id === payload.employeeId);
    if (!emp) return toast.error('Employee not found');
    const startDt = new Date(`${payload.date}T${payload.start}:00`);
    const endDt = payload.end ? new Date(`${payload.date}T${payload.end}:00`) : null;
    if (endDt && endDt <= startDt) {
      toast.error('End time must be after start time');
      return;
    }
    const hours = endDt ? parseFloat((minutesBetween(startDt.toISOString(), endDt.toISOString()) / 60).toFixed(2)) : 0;
    const rate = parseFloat(emp.hourlyRate) || 0;
    const entry = normalizeEntry({
      id: `t_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      employeeId: emp.id,
      employeeName: emp.name,
      startTime: startDt.toISOString(),
      endTime: endDt ? endDt.toISOString() : null,
      hours,
      pay: parseFloat((hours * rate).toFixed(2)),
      hourlyRate: rate,
      note: payload.note || '',
      project: 'General Work',
      task: 'Manual Entry',
      job: payload.job || '',
      managerNote: payload.note || '',
      gpsClockIn: null,
      gpsClockOut: null,
      breaks: [],
      auditLog: [auditEvent(user, 'created', { source: [null, 'manual'] })],
    });
    saveEntries((prev) => [...prev, entry]);
    setShowAddShift(false);
    toast.success(`Shift added for ${emp.name}`);
  };

  // ---------- THEME (CSS-in-JS, scoped to this page) ----------
  const C = dark
    ? {
        page: '#0b1220', surface: '#111827', surfaceAlt: '#1f2937', border: '#1f2937',
        text: '#f3f4f6', subtext: '#9ca3af', primary: '#3b82f6', primarySoft: 'rgba(59,130,246,0.15)',
        rowHover: 'rgba(255,255,255,0.04)',
      }
    : {
        page: '#f8fafc', surface: '#ffffff', surfaceAlt: '#f8fafc', border: '#e5e7eb',
        text: '#111827', subtext: '#6b7280', primary: '#0073ea', primarySoft: 'rgba(0,115,234,0.08)',
        rowHover: 'rgba(0,115,234,0.04)',
      };

  return (
    <div
      data-testid="time-clock-page"
      style={{
        background: C.page,
        color: C.text,
        minHeight: 'calc(100vh - 2rem)',
        margin: '-1rem',
        padding: '1.25rem 1.5rem',
        transition: 'background 0.2s',
      }}
    >
      {/* ============ TOP BAR ============ */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          flexWrap: 'wrap',
          marginBottom: '1.25rem',
          // Sit above Sonner toasts (z-index ~10000) so quick clicks on the
          // dark toggle / Add Shift right after a clock-out toast still
          // register on the button itself, not the toast overlay.
          position: 'relative',
          zIndex: 10001,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: '0 0 auto' }}>
          <IconTile icon={Clock} tint={TINT.timeClock} size={44} />
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: C.text }}>Time Clock</h1>
            <div style={{ fontSize: '0.8rem', color: C.subtext }}>
              {visibleEmployees.length} {visibleEmployees.length === 1 ? 'employee' : 'employees'} •{' '}
              {Object.keys(entryByEmpId).filter((id) => !entryByEmpId[id].endTime).length} active now
            </div>
          </div>
        </div>

        <div style={{ flex: 1 }} />
      </div>

      {/* ============ CONFLICT BANNER ============ */}
      {(isAdmin || isManager) && (() => {
        // Surface up to 3 most recent shift overlaps so admins can clean
        // them up. Don't render anything if no conflicts.
        const conflicts = detectConflicts(entries);
        if (conflicts.length === 0) return null;
        return (
          <div
            data-testid="conflict-banner"
            style={{
              background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 12,
              padding: '0.85rem 1.1rem', marginBottom: '1rem',
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}
          >
            <AlertTriangle size={18} color="#c2410c" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1, fontSize: '0.85rem', color: '#7c2d12' }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                {conflicts.length} shift conflict{conflicts.length > 1 ? 's' : ''} detected
              </div>
              {conflicts.slice(0, 3).map((c, i) => (
                <div key={`${c.employeeName}-${c.a?.startTime || ''}-${c.b?.startTime || ''}`} data-testid={`conflict-${i}`} style={{ fontSize: '0.8rem' }}>
                  • {c.employeeName} has overlapping shifts on{' '}
                  {new Date(c.a.startTime).toLocaleDateString()} ({Math.round(c.overlapMin)}m overlap)
                </div>
              ))}
              {conflicts.length > 3 && (
                <div style={{ fontSize: '0.75rem', marginTop: 4 }}>
                  + {conflicts.length - 3} more — open the Timesheets tab to resolve.
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ============ TABS (Connecteam-style segmented control) ============ */}
      <style>{'@keyframes tcPulse{0%,100%{opacity:1}50%{opacity:0.35}}'}</style>
      <div
        style={{
          display: 'inline-flex', gap: 6, padding: 6, marginBottom: '1.25rem',
          background: dark ? '#111827' : '#f1f5f9',
          border: `1px solid ${C.border}`,
          borderRadius: 14,
          position: 'relative', zIndex: 10000,
          flexWrap: 'wrap',
          boxShadow: dark ? 'inset 0 1px 0 rgba(255,255,255,0.03)' : 'inset 0 1px 2px rgba(15,23,42,0.04)',
        }}
        data-testid="time-clock-tabs"
      >
        {[
          { id: 'today', label: 'Today', icon: LayoutGrid, roles: ['admin', 'manager', 'employee', 'owner'] },
          { id: 'timesheets', label: 'Timesheets', icon: ClipboardList, roles: ['admin', 'manager', 'employee', 'owner'] },
          { id: 'payroll', label: 'Payroll', icon: DollarSign, roles: ['admin', 'manager', 'owner'] },
          { id: 'insights', label: 'Insights', icon: BarChart3, roles: ['admin', 'manager', 'owner'] },
        ]
          .filter((t) => t.roles.includes(user?.role || 'admin'))
          .map(({ id, label, icon: Icon }) => {
          const active = tab === id;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`tab-${id}`}
              style={{
                background: active ? '#0073ea' : 'transparent',
                border: 'none', cursor: 'pointer',
                padding: '0.6rem 1.25rem',
                borderRadius: 10,
                color: active ? '#ffffff' : (dark ? '#e5e7eb' : '#334155'),
                fontWeight: 700, fontSize: '0.9rem',
                display: 'inline-flex', alignItems: 'center', gap: 8,
                boxShadow: active ? '0 4px 12px rgba(0,115,234,0.35)' : 'none',
                transition: 'background 0.18s, color 0.18s, box-shadow 0.18s',
                letterSpacing: 0.1,
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = dark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.05)';
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = 'transparent';
              }}
            >
              <Icon size={17} />
              {label}
            </button>
          );
        })}

        {/* Dark mode toggle — same visual language as the tabs */}
        <button
          type="button"
          onClick={() => setDark((d) => !d)}
          data-testid="time-clock-dark-toggle"
          title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            background: dark ? '#0073ea' : 'transparent',
            border: 'none', cursor: 'pointer',
            padding: '0.6rem 0.85rem',
            borderRadius: 10,
            color: dark ? '#ffffff' : (dark ? '#e5e7eb' : '#334155'),
            fontWeight: 700, fontSize: '0.9rem',
            display: 'inline-flex', alignItems: 'center', gap: 8,
            boxShadow: dark ? '0 4px 12px rgba(0,115,234,0.35)' : 'none',
            transition: 'background 0.18s, color 0.18s, box-shadow 0.18s',
            marginLeft: 4,
          }}
          onMouseEnter={(e) => {
            if (!dark) e.currentTarget.style.background = 'rgba(15,23,42,0.05)';
          }}
          onMouseLeave={(e) => {
            if (!dark) e.currentTarget.style.background = 'transparent';
          }}
        >
          {dark ? <Sun size={17} /> : <Moon size={17} />}
          {dark ? 'Light' : 'Dark'}
        </button>
      </div>

      {tab === 'timesheets' ? (
        <TimesheetsTab
          C={C}
          employees={employees}
          entries={entries}
          saveEntries={saveEntries}
          ptoRequests={ptoRequests}
          savePtoRequests={savePtoRequests}
        />
      ) : tab === 'payroll' ? (
        <PayrollView C={C} employees={employees} entries={entries} saveEmployees={saveEmployees} />
      ) : tab === 'insights' ? (
        <InsightsView C={C} employees={employees} entries={entries} />
      ) : (
      <>
      {/* ============ TODAY KPI CARDS ============ */}
      {(() => {
        let working = 0; let onBreak = 0; let done = 0; let totalMin = 0;
        visibleEmployees.forEach((emp) => {
          const e = entryByEmpId[emp.id];
          const status = computeStatus(e);
          if (status === 'Working') working += 1;
          else if (status === 'On Break') onBreak += 1;
          else if (e?.endTime) done += 1;
          if (e) totalMin += liveWorkedMinutes(e);
        });
        return (
          <KpiRow testId="today-kpi-row">
            <KpiCard C={C} icon={Play} color="#10b981" label="Currently Working" value={working} sub={`of ${visibleEmployees.length} employee${visibleEmployees.length === 1 ? '' : 's'}`} testId="kpi-working" />
            <KpiCard C={C} icon={Coffee} color="#f59e0b" label="On Break" value={onBreak} testId="kpi-on-break" />
            <KpiCard C={C} icon={CheckCircle2} color="#64748b" label="Clocked Out" value={done} testId="kpi-clocked-out" />
            <KpiCard C={C} icon={Clock} color="#0073ea" label="Total Hours Today" value={formatDuration(totalMin)} sub="updates live" testId="kpi-total-hours" />
          </KpiRow>
        );
      })()}

      {/* ============ EMPLOYEE TABLE ============ */}
      <div
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 14,
          overflow: 'hidden',
        }}
        data-testid="time-clock-table-card"
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: C.surfaceAlt, color: C.subtext, textAlign: 'left' }}>
                <th style={th()}>Employee</th>
                <th style={th()}>Crew</th>
                <th style={th()}>Job</th>
                <th style={th()}>Clock In</th>
                <th style={th()}>Clock Out</th>
                <th style={th()}>Break</th>
                <th style={th()}>Total</th>
                <th style={th()}>Status</th>
                <th style={th()}>GPS</th>
                <th style={th(true)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleEmployees.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: '2.5rem', textAlign: 'center', color: C.subtext }}>
                    <UsersIcon size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
                    <div>No employees in view</div>
                  </td>
                </tr>
              ) : (
                visibleEmployees.map((emp) => {
                  const entry = entryByEmpId[emp.id];
                  const status = computeStatus(entry);
                  const mins = entry ? liveWorkedMinutes(entry) : 0;
                  const breakMins = entry ? totalBreakMinutes(entry.breaks) : 0;
                  const gps = entry?.gpsClockIn;
                  return (
                    <tr
                      key={emp.id}
                      data-testid={`time-clock-row-${emp.id}`}
                      style={{ borderTop: `1px solid ${C.border}` }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = C.rowHover)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={td()}>
                        <button
                          type="button"
                          data-testid={`time-clock-emp-${emp.id}`}
                          onClick={() => setDrawerEmpId(emp.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            background: 'transparent', border: 'none', padding: '4px 6px',
                            margin: '-4px -6px',
                            borderRadius: 8,
                            cursor: 'pointer', textAlign: 'left',
                            transition: 'background 0.15s, transform 0.12s',
                            color: 'inherit',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(0,115,234,0.06)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.background = 'rgba(0,115,234,0.06)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          {emp.photo ? (
                            <img
                              src={emp.photo}
                              alt=""
                              style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 32, height: 32, borderRadius: '50%',
                                background: avatarColor(emp.id), color: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.75rem', fontWeight: 600,
                              }}
                            >
                              {initials(emp.name)}
                            </div>
                          )}
                          <div>
                            <div
                              style={{
                                fontWeight: 600, color: C.text,
                                transition: 'color 0.15s',
                              }}
                              className="time-clock-emp-name"
                            >
                              {emp.name}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: C.subtext }}>
                              {emp.role === 'manager' ? 'Manager' : emp.role === 'admin' ? 'Admin' : 'Cleaner'}
                            </div>
                          </div>
                        </button>
                      </td>
                      <td style={td()}>
                        {emp.crewId ? (
                          <span
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              padding: '0.15rem 0.55rem', borderRadius: 999,
                              background: `${getCrewColor(emp.crewId)}1a`,
                              color: getCrewColor(emp.crewId),
                              border: `1px solid ${getCrewColor(emp.crewId)}40`,
                              fontSize: '0.72rem', fontWeight: 600,
                            }}
                          >
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: getCrewColor(emp.crewId) }} />
                            {getCrewName(emp.crewId)}
                          </span>
                        ) : (
                          <span style={{ color: C.subtext }}>—</span>
                        )}
                      </td>
                      <td style={td()}>
                        {entry?.job ? (() => {
                          // Hash job name to a consistent tint so the same job
                          // gets the same colored pill across rows / refreshes.
                          const palette = ['#0073ea', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#0ea5e9', '#22c55e', '#ef4444'];
                          let h = 0;
                          for (let i = 0; i < entry.job.length; i += 1) h = ((h << 5) - h + entry.job.charCodeAt(i)) | 0;
                          const c = palette[Math.abs(h) % palette.length];
                          return (
                            <span
                              data-testid={`job-pill-${emp.id}`}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '0.18rem 0.65rem', borderRadius: 999,
                                background: `${c}14`, color: c,
                                border: `1px solid ${c}33`,
                                fontSize: '0.72rem', fontWeight: 600,
                                maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}
                              title={entry.job}
                            >
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: c, flexShrink: 0 }} />
                              {entry.job}
                            </span>
                          );
                        })() : (
                          <span style={{ color: C.subtext }}>—</span>
                        )}
                      </td>
                      <td style={td()}>{entry?.startTime ? formatHM(entry.startTime) : '—'}</td>
                      <td style={td()}>{entry?.endTime ? formatHM(entry.endTime) : '—'}</td>
                      <td style={td()}>
                        <span style={{ color: breakMins > 0 ? C.text : C.subtext }}>
                          {breakMins > 0 ? formatDuration(breakMins) : '—'}
                        </span>
                      </td>
                      <td style={td()}>
                        <span style={{ fontWeight: 600 }}>{mins > 0 ? formatDuration(mins) : '—'}</span>
                      </td>
                      <td style={td()}>
                        <StatusPill status={status} mins={status === 'Working' ? mins : 0} />
                      </td>
                      <td style={td()}>
                        {gps ? (
                          <a
                            href={`https://www.google.com/maps?q=${gps.lat},${gps.lng}`}
                            target="_blank"
                            rel="noreferrer"
                            title={`±${gps.accuracy}m`}
                            style={{
                              color: '#10b981', display: 'inline-flex', alignItems: 'center', gap: 4,
                              textDecoration: 'none',
                            }}
                          >
                            <MapPin size={14} />
                            <span style={{ fontSize: '0.72rem' }}>✓</span>
                          </a>
                        ) : (
                          <span style={{ color: C.subtext }}>—</span>
                        )}
                      </td>
                      <td style={td(true)}>
                        <RowActions
                          entry={entry}
                          emp={emp}
                          canActSelf={isEmployee && emp.email === user?.email}
                          canActOther={isAdmin || isManager}
                          C={C}
                          onClockIn={() => handleClockIn(emp)}
                          onClockOut={() => handleClockOut(entry)}
                          onStartBreak={() => handleStartBreak(entry)}
                          onEndBreak={() => handleEndBreak(entry)}
                          onChat={(isAdmin || isManager) ? () => {
                            // Placeholder until the in-app messaging surface ships.
                            // For now, open a mailto: so managers can still reach the cleaner.
                            if (emp.email) {
                              window.location.href = `mailto:${emp.email}?subject=Quick message`;
                            } else {
                              toast.info('No email on file for this employee');
                            }
                          } : undefined}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============ ADD SHIFT MODAL ============ */}
      {showAddShift && (
        <AddShiftModal
          C={C}
          employees={employees}
          defaultDate={date}
          onClose={() => setShowAddShift(false)}
          onSave={handleAddShift}
        />
      )}

      {/* ============ EMPLOYEE DETAIL DRAWER ============ */}
      <EmployeeDrawer
        open={Boolean(drawerEmpId)}
        employee={employees.find((e) => e.id === drawerEmpId) || null}
        entries={entries}
        todayEntry={entries.find(
          (e) => e.employeeId === drawerEmpId && !e.endTime,
        )}
        isMobile={isMobile}
        onClose={() => setDrawerEmpId(null)}
        onEdit={() => {
          setDrawerEmpId(null);
          navigate('/employees');
        }}
        onMessage={() => {
          const emp = employees.find((e) => e.id === drawerEmpId);
          if (emp?.email) {
            window.location.href = `mailto:${emp.email}`;
          } else {
            toast.info('No email on file for this employee');
          }
        }}
        onViewSchedule={() => {
          setDrawerEmpId(null);
          navigate('/scheduling');
        }}
        onViewPayroll={() => {
          setDrawerEmpId(null);
          setTab('payroll');
        }}
        onViewTimesheet={() => {
          // From the drawer, jump to the per-employee Detail view inside
          // the Timesheets tab. drillEmpId is owned by the inner
          // <TimesheetsTab>, so we simulate the click by navigating with
          // a sentinel and letting the tab read its initial state.
          setDrawerEmpId(null);
          setTab('timesheets');
          // Persist the picked employee so the Detail view auto-opens to them.
          try {
            sessionStorage.setItem('timesheet_drill_emp_id', drawerEmpId);
            sessionStorage.setItem('timesheet_subtab', 'detail');
          } catch (e) {
            // sessionStorage quota exceeded or blocked by strict privacy settings —
            // the drill-in still works, just without pre-selection.
            console.debug('[timesheet] sessionStorage write failed', e);
          }
        }}
      />
      </>
      )}
    </div>
  );
};

// ====== Small components / helpers ======

const th = (last = false) => ({
  padding: '0.7rem 0.9rem',
  fontWeight: 600,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  textAlign: last ? 'right' : 'left',
  whiteSpace: 'nowrap',
});
const td = (last = false) => ({
  padding: '0.7rem 0.9rem',
  textAlign: last ? 'right' : 'left',
  verticalAlign: 'middle',
});

const StatusPill = ({ status, mins = 0 }) => {
  const styles = {
    Working: { bg: 'rgba(16,185,129,0.15)', fg: '#10b981', label: 'Working' },
    'On Break': { bg: 'rgba(245,158,11,0.15)', fg: '#f59e0b', label: 'On Break' },
    Off: { bg: 'rgba(156,163,175,0.18)', fg: '#9ca3af', label: 'Off' },
  };
  const s = styles[status] || styles.Off;
  const live = status === 'Working' || status === 'On Break';
  return (
    <span
      data-testid={`status-pill-${status.replace(' ', '-').toLowerCase()}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '0.2rem 0.6rem', borderRadius: 999,
        background: s.bg, color: s.fg,
        fontSize: '0.72rem', fontWeight: 600,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.fg, animation: live ? 'tcPulse 1.6s ease-in-out infinite' : 'none' }} />
      {s.label}
      {status === 'Working' && mins > 0 && (
        <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.85 }}>· {formatDuration(mins)}</span>
      )}
    </span>
  );
};

const RowActions = ({ entry, emp, canActSelf, canActOther, C, onClockIn, onClockOut, onStartBreak, onEndBreak, onChat }) => {
  const allowed = canActOther || canActSelf;
  if (!allowed) return <span style={{ color: C.subtext, fontSize: '0.72rem' }}>—</span>;

  const chatBtn = onChat ? (
    <button
      type="button"
      onClick={onChat}
      data-testid={`chat-${emp.id}`}
      title={`Message ${emp.name}`}
      aria-label={`Message ${emp.name}`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 30, height: 30, borderRadius: 8,
        background: 'transparent', color: C.subtext, border: `1px solid ${C.border}`,
        cursor: 'pointer',
      }}
    >
      <MessageSquare size={14} />
    </button>
  ) : null;

  if (!entry || entry.endTime) {
    return (
      <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
        {chatBtn}
        <button
          onClick={onClockIn}
          data-testid={`clock-in-${emp.id}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: '#10b981', color: '#fff', border: 'none', borderRadius: 8,
            padding: '0.32rem 0.65rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
          }}
        >
          <Play size={12} /> Clock In
        </button>
      </div>
    );
  }

  const onBreak = (entry.breaks || []).some((b) => !b.endTime);

  return (
    <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      {chatBtn}
      {onBreak ? (
        <button
          onClick={onEndBreak}
          data-testid={`end-break-${emp.id}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: C.surface, color: '#f59e0b', border: '1px solid #f59e0b',
            borderRadius: 8, padding: '0.32rem 0.55rem',
            fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
          }}
        >
          <Play size={12} /> Resume
        </button>
      ) : (
        <button
          onClick={onStartBreak}
          data-testid={`start-break-${emp.id}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: 'transparent', color: '#f59e0b', border: '1px solid #f59e0b',
            borderRadius: 8, padding: '0.32rem 0.55rem',
            fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
          }}
        >
          <Coffee size={12} /> Break
        </button>
      )}
      <button
        onClick={onClockOut}
        data-testid={`clock-out-${emp.id}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8,
          padding: '0.32rem 0.65rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
        }}
      >
        <Square size={12} /> Clock Out
      </button>
    </div>
  );
};

// ----- Add Shift Modal -----
const AddShiftModal = ({ C, employees, defaultDate, onClose, onSave }) => {
  const [form, setForm] = useState({
    employeeId: '', date: defaultDate, start: '09:00', end: '17:00', job: '', note: '',
  });
  return (
    <div
      onClick={onClose}
      data-testid="add-shift-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface, color: C.text, borderRadius: 14,
          padding: '1.25rem 1.5rem', width: '100%', maxWidth: 460,
          border: `1px solid ${C.border}`,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Add Shift</h2>
          <button
            onClick={onClose}
            data-testid="close-add-shift"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.text }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <Field label="Employee" C={C}>
            <select
              value={form.employeeId}
              onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
              data-testid="add-shift-employee"
              style={input(C)}
            >
              <option value="">Select employee...</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Date" C={C}>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              style={input(C)}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Field label="Start" C={C}>
              <input
                type="time"
                value={form.start}
                onChange={(e) => setForm({ ...form, start: e.target.value })}
                style={input(C)}
              />
            </Field>
            <Field label="End (optional)" C={C}>
              <input
                type="time"
                value={form.end}
                onChange={(e) => setForm({ ...form, end: e.target.value })}
                style={input(C)}
              />
            </Field>
          </div>

          <Field label="Job" C={C}>
            <input
              type="text"
              placeholder="e.g. House Cleaning"
              value={form.job}
              onChange={(e) => setForm({ ...form, job: e.target.value })}
              style={input(C)}
            />
          </Field>

          <Field label="Manager note (optional)" C={C}>
            <textarea
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              rows={2}
              style={{ ...input(C), resize: 'vertical' }}
            />
          </Field>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1rem' }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.55rem 1rem', borderRadius: 8, border: `1px solid ${C.border}`,
              background: 'transparent', color: C.text, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (!form.employeeId) return toast.error('Pick an employee');
              if (!form.start) return toast.error('Start time required');
              onSave(form);
            }}
            data-testid="save-shift-button"
            style={{
              padding: '0.55rem 1rem', borderRadius: 8, border: 'none',
              background: C.primary, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
            }}
          >
            Save Shift
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children, C }) => (
  <label style={{ display: 'block' }}>
    <div style={{ fontSize: '0.72rem', color: C.subtext, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </div>
    {children}
  </label>
);

const input = (C) => ({
  width: '100%', padding: '0.55rem 0.7rem',
  background: C.surface, color: C.text,
  border: `1px solid ${C.border}`, borderRadius: 8,
  fontSize: '0.85rem', outline: 'none',
});

export default TimeTracking;

/**
 * Timesheets tab — top-level sub-toggle between the heat-map grid (default,
 * Connecteam-style multi-employee × multi-day overview) and the existing
 * per-employee Detail view. Clicking an employee row in the heat-map jumps
 * into the Detail view pre-selected to that employee.
 */
const TimesheetsTab = ({ C, employees, entries, saveEntries, ptoRequests, savePtoRequests }) => {
  // On mount, hydrate from sessionStorage so the drawer's "View full timesheet"
  // action lands us on the Detail sub-view pre-selected to the right employee.
  const [drillEmpId] = useState(() => {
    try {
      const id = sessionStorage.getItem('timesheet_drill_emp_id');
      sessionStorage.removeItem('timesheet_drill_emp_id');
      return id;
    } catch { return null; }
  });

  return (
    <div data-testid="timesheets-tab">
      {/* Weekly KPI cards */}
      {(() => {
        const ws = startOfWeek(new Date());
        const weekEnd = new Date(ws); weekEnd.setDate(ws.getDate() + 7);
        let totalPaidMin = 0; let ptoMin = 0;
        employees.forEach((emp) => {
          const s = computeWeeklyStats(entries, emp.id, ws);
          totalPaidMin += s.totalPaidMin;
          ptoMin += s.ptoMin;
        });
        const pendingApprovals = entries.filter((e) => {
          const d = new Date(e.startTime);
          return d >= ws && d < weekEnd && e.endTime && e.approvalStatus === 'pending';
        }).length;
        const pendingPto = ptoRequests.filter((r) => r.status === 'pending').length;
        return (
          <KpiRow testId="timesheets-kpi-row">
            <KpiCard C={C} icon={Clock} color="#0073ea" label="Paid Hours This Week" value={formatDuration(totalPaidMin)} testId="kpi-week-paid-hours" />
            <KpiCard C={C} icon={CalendarCheck} color="#8b5cf6" label="PTO Hours" value={formatDuration(ptoMin)} testId="kpi-week-pto" />
            <KpiCard C={C} icon={Hourglass} color="#f59e0b" label="Pending Approvals" value={pendingApprovals} sub="shifts this week" testId="kpi-pending-approvals" />
            <KpiCard C={C} icon={ClipboardList} color="#10b981" label="PTO Requests" value={pendingPto} sub="awaiting review" testId="kpi-pending-pto" />
          </KpiRow>
        );
      })()}

      <TimesheetsView
        C={C}
        employees={employees}
        entries={entries}
        saveEntries={saveEntries}
        ptoRequests={ptoRequests}
        savePtoRequests={savePtoRequests}
        preselectedEmployeeId={drillEmpId}
      />
    </div>
  );
};

